import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { useQuery } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Flex } from "@radix-ui/themes";
import Lowlight from "react-lowlight";
import styled from "@emotion/styled";
import { atlasRoutes } from "@/app/router/routes";

export const CapturedEmailsViewPage: FC = () => {
  const navigate = useNavigate();
  const { capturedEmailId } = useParams<{ capturedEmailId: Uuid }>();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [preview, setPreview] = useState<CapturedEmailPreview | null>(null);

  if (!capturedEmailId) {
    return <div>Invalid ID</div>;
  }

  const result = useQuery({
    queryKey: ["captured-email-by-id", capturedEmailId],
    queryFn: () =>
      api.get("/internal/v1/captured-emails/{captured_email_id}", {
        params: {
          path: {
            captured_email_id: capturedEmailId,
          },
        },
      }),
    staleTime: 60 * 1000,
  });

  // no-op refresh retained if needed later

  const handleFetchSignedUrl = async () => {
    try {
      const resp = await api.get(
        "/internal/v1/captured-emails/{captured_email_id}/signed-raw-url",
        {
          params: { path: { captured_email_id: capturedEmailId } },
        }
      );
      setSignedUrl(resp.data?.raw_download_url ?? null);
    } catch (err) {
      alert(`Failed to fetch URL: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleFetchPreview = async () => {
    try {
      const resp = await api.get(
        "/internal/v1/captured-emails/{captured_email_id}/preview-content",
        {
          params: { path: { captured_email_id: capturedEmailId } },
        }
      );
      setPreview((resp.data as CapturedEmailPreview) ?? null);
    } catch (err) {
      alert(`Failed to fetch preview: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleViewAssociatedSession = () => {
    const associatedSessionId = result.data?.data?.associated_session_id;
    if (!associatedSessionId) {
      alert("No associated session.");
      return;
    }

    navigate(atlasRoutes.sessionEvents({ params: { sessionId: associatedSessionId } }).path);
  };

  if (result.isLoading) {
    return <div>Loading...</div>;
  }

  if (result.isError || !result.data) {
    return <div>Error: {result.error?.message ?? "Unknown error"}</div>;
  }

  return (
    <Container>
      <br />
      <ButtonGroup>
        <Button variant="soft" onClick={handleViewAssociatedSession}>
          View Associated Session
        </Button>
        <Button variant="outline" onClick={handleFetchSignedUrl}>
          Fetch Raw Signed URL
        </Button>
        <Button variant="outline" onClick={handleFetchPreview}>
          Fetch Preview
        </Button>
        {signedUrl && (
          <a href={signedUrl} target="_blank" rel="noreferrer">
            Download Raw
          </a>
        )}
      </ButtonGroup>

      <br />
      <h1>View Captured Email</h1>
      <Lowlight language="json" value={JSON.stringify(result.data, null, 2)} markers={[]} />
      {preview && (
        <>
          <h2>Preview</h2>
          <Lowlight language="json" value={JSON.stringify(preview, null, 2)} markers={[]} />
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const ButtonGroup = styled(Flex)`
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;
type CapturedEmailPreview = {
  subject: string | null;
  sender_name: string | null;
  sender_email: string | null;
  forwarded_html_present: boolean;
  original_html_present: boolean;
  cleaned_html: string | null;
};
